/**
 * Email subscription group (SendGrid ASM) catalog service.
 *
 * CRUD over public.email_subscription_groups. RLS permits any authenticated user to
 * READ and only superadmin/admin/it to WRITE; the UI relies on that gate and surfaces
 * friendly messages for the DB constraints (positive id, slug format, one active default).
 */
import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export interface SubscriptionGroup {
  id: UUID;
  sendgrid_group_id: number;
  slug: string;
  name: string;
  description: string | null;
  is_active: boolean;
  display_on_preferences: boolean;
  is_default_marketing: boolean;
  sort_order: number;
  provider_account: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionGroupInput {
  sendgrid_group_id: number;
  slug: string;
  name: string;
  description?: string | null;
  is_active?: boolean;
  display_on_preferences?: boolean;
  is_default_marketing?: boolean;
  sort_order?: number;
  provider_account?: string | null;
}

const SLUG_RE = /^[a-z0-9][a-z0-9_-]*$/;

function friendlyError(
  error: { message?: string; code?: string } | null,
): string {
  const msg = error?.message ?? "Unknown error";
  if (
    /uq_esg_sendgrid_group_id|sendgrid_group_id/i.test(msg) &&
    /duplicate|unique/i.test(msg)
  ) {
    return "That SendGrid group ID is already in the catalog.";
  }
  if (/uq_esg_slug|slug/i.test(msg) && /duplicate|unique/i.test(msg)) {
    return "That slug is already in use.";
  }
  if (/uq_esg_one_default_marketing/i.test(msg)) {
    return "Another active group is already the default marketing category — unset it first.";
  }
  if (/esg_slug_format/i.test(msg)) {
    return "Slug must be lowercase letters, numbers, hyphens or underscores (starting with a letter or number).";
  }
  if (/esg_sendgrid_group_id_positive/i.test(msg)) {
    return "SendGrid group ID must be a positive integer.";
  }
  if (/row-level security|permission denied/i.test(msg)) {
    return "You don't have permission to change the subscription-group catalog.";
  }
  return msg;
}

/** Client-side guard so we fail fast before the round-trip. */
export function validateGroupInput(
  input: SubscriptionGroupInput,
): string | null {
  if (!input.name?.trim()) {
    return "Name is required.";
  }
  if (!SLUG_RE.test(input.slug ?? "")) {
    return "Slug must be lowercase letters, numbers, hyphens or underscores (starting with a letter or number).";
  }
  if (
    !Number.isInteger(input.sendgrid_group_id) ||
    input.sendgrid_group_id <= 0
  ) {
    return "SendGrid group ID must be a positive integer.";
  }
  return null;
}

export async function listGroups(): Promise<SubscriptionGroup[]> {
  const { data, error } = await supabase
    .from("email_subscription_groups")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) {
    throw new Error(friendlyError(error));
  }
  return (data ?? []) as SubscriptionGroup[];
}

export async function listActiveGroups(): Promise<SubscriptionGroup[]> {
  const { data, error } = await supabase
    .from("email_subscription_groups")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) {
    throw new Error(friendlyError(error));
  }
  return (data ?? []) as SubscriptionGroup[];
}

export async function createGroup(
  input: SubscriptionGroupInput,
): Promise<SubscriptionGroup> {
  const invalid = validateGroupInput(input);
  if (invalid) {
    throw new Error(invalid);
  }
  const { data, error } = await supabase
    .from("email_subscription_groups")
    .insert({
      sendgrid_group_id: input.sendgrid_group_id,
      slug: input.slug.trim(),
      name: input.name.trim(),
      description: input.description?.trim() || null,
      is_active: input.is_active ?? true,
      display_on_preferences: input.display_on_preferences ?? true,
      is_default_marketing: input.is_default_marketing ?? false,
      sort_order: input.sort_order ?? 0,
      provider_account: input.provider_account?.trim() || null,
    })
    .select("*")
    .single();
  if (error) {
    throw new Error(friendlyError(error));
  }
  return data as SubscriptionGroup;
}

export async function updateGroup(
  id: UUID,
  patch: Partial<SubscriptionGroupInput>,
): Promise<SubscriptionGroup> {
  if (
    patch.slug !== undefined ||
    patch.sendgrid_group_id !== undefined ||
    patch.name !== undefined
  ) {
    const invalid = validateGroupInput({
      name: patch.name ?? "placeholder",
      slug: patch.slug ?? "placeholder",
      sendgrid_group_id: patch.sendgrid_group_id ?? 1,
    });
    // Only enforce the fields actually present.
    if (
      invalid &&
      ((patch.slug !== undefined && /Slug/.test(invalid)) ||
        (patch.sendgrid_group_id !== undefined && /SendGrid/.test(invalid)) ||
        (patch.name !== undefined && /Name/.test(invalid)))
    ) {
      throw new Error(invalid);
    }
  }
  const clean: Record<string, unknown> = { ...patch };
  // sendgrid_group_id is immutable (audit R2-B1) — never send it on an update. The DB
  // trigger rejects a change regardless; this stops the client from even attempting it
  // (e.g. a stale form value) so a legitimate name/desc edit isn't blocked by the trigger.
  delete clean.sendgrid_group_id;
  if (typeof clean.slug === "string") {
    clean.slug = clean.slug.trim();
  }
  if (typeof clean.name === "string") {
    clean.name = clean.name.trim();
  }
  if (typeof clean.description === "string") {
    clean.description = (clean.description as string).trim() || null;
  }
  if (typeof clean.provider_account === "string") {
    clean.provider_account = (clean.provider_account as string).trim() || null;
  }
  const { data, error } = await supabase
    .from("email_subscription_groups")
    .update(clean)
    .eq("id", id)
    .select("*")
    .single();
  if (error) {
    throw new Error(friendlyError(error));
  }
  return data as SubscriptionGroup;
}

export async function setActive(id: UUID, active: boolean): Promise<void> {
  const { error } = await supabase
    .from("email_subscription_groups")
    .update({ is_active: active })
    .eq("id", id);
  if (error) {
    throw new Error(friendlyError(error));
  }
}
